import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  guessMapping,
  IMPORT_COLUMN_KINDS,
  looksLikeHeader,
  mapRow,
  parseCsv,
  type ImportColumnKind,
  type ImportMapping,
} from "@magarac/shared/import";
import {
  DUPLICATE_MODES,
  googleImportStartSchema,
  googleImportStatusSchema,
  IMPORT_BATCH_MAX,
  importResultSchema,
  importTableSchema,
  type DuplicateMode,
  type ImportResult,
  type ImportRow,
} from "@magarac/shared/schemas/import";
import { Badge, Banner, Button, Card, PageHeader, Select, Spinner } from "@magarac/ui";
import { HelpIcon } from "../../components/HelpIcon";
import { api } from "../../lib/api";
import { messageFor } from "../../lib/errors";
import { formatPhone } from "../../lib/format";
import { useTenant } from "../../lib/tenant";

export const Route = createFileRoute("/_app/import")({
  validateSearch: z.object({ google: z.string().optional(), google_error: z.string().optional() }),
  component: ImportPage,
});

const KIND_LABEL: Record<ImportColumnKind, string> = {
  ignore: "Skip this column",
  firstName: "First name",
  lastName: "Last name",
  fullName: "Full name",
  nameAndPhone: "Name and phone together",
  company: "Company",
  phone: "Phone",
  phone2: "Second phone",
  email: "Email",
  notes: "Notes",
  tags: "Tags",
  line1: "Street address",
  line2: "Apt or unit",
  city: "City",
  state: "State",
  postalCode: "ZIP",
};

const MODE_LABEL: Record<DuplicateMode, { label: string; help: string }> = {
  skip: { label: "Skip them", help: "Keep the customer you already have and ignore the row." },
  update: { label: "Update them", help: "Fill in the existing customer with what is in the row." },
  create: {
    label: "Add them anyway",
    help: "Create a second customer even if the phone or email matches.",
  },
};

interface Table {
  headers: string[];
  rows: string[][];
  /** Spreadsheet line of each row, so problems point at the line people see in Excel. */
  lines?: number[];
}

/**
 * Excel on Windows saves "CSV" in the old Windows encoding (and "Unicode text" as UTF-16), so
 * reading everything as UTF-8 turns "José" into "Jos�". Try UTF-8 strictly, then fall back.
 */
async function readText(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes);
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

/** CRM-26 CSV import and CRM-27 Google Contacts import. Screen key: import */
function ImportPage() {
  const search = Route.useSearch();
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const [csvTable, setCsvTable] = useState<Table | null>(null);
  const [csvHasHeader, setCsvHasHeader] = useState(true);
  const [mappingOverride, setMapping] = useState<ImportMapping | null>(null);
  const [dismissedGoogle, setDismissedGoogle] = useState(false);
  const [mode, setMode] = useState<DuplicateMode>("skip");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(
    search.google_error === "expired"
      ? "Connecting to Google took too long or you were signed out. Please start again."
      : search.google_error === "denied"
        ? "Google access was not allowed, so nothing was imported. You can try again any time."
        : search.google_error
          ? "Google import did not finish. Please try again."
          : null,
  );

  const google = useQuery({
    queryKey: ["import", "google", "status"],
    queryFn: () => api.get("/import/google/status", googleImportStatusSchema),
    enabled: Boolean(active),
  });
  const googleTable = useQuery({
    queryKey: ["import", "google", search.google],
    queryFn: () => api.get(`/import/google/${search.google}`, importTableSchema),
    enabled: Boolean(active && search.google),
    retry: false,
  });
  // A spreadsheet the office just chose wins; otherwise the Google contacts we came back with.
  const table: Table | null =
    csvTable ?? (!dismissedGoogle && googleTable.data ? googleTable.data : null);
  const source: "csv" | "google" = csvTable ? "csv" : "google";
  const mapping: ImportMapping = useMemo(
    () =>
      mappingOverride ??
      (table
        ? source === "csv" && !csvHasHeader
          ? table.headers.map(() => "ignore" as const)
          : guessMapping(table.headers)
        : []),
    [mappingOverride, table, source, csvHasHeader],
  );
  const shownError = error ?? (googleTable.error ? messageFor(googleTable.error) : null);
  const startOver = () => {
    setCsvTable(null);
    setMapping(null);
    setDismissedGoogle(true);
  };

  const startGoogle = useMutation({
    mutationFn: () => api.post("/import/google/start", {}, googleImportStartSchema),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (err) => setError(messageFor(err)),
  });

  const readFile = async (file: File) => {
    setError(null);
    setResult(null);
    const text = await readText(file);
    const numbered = parseCsv(text)
      .map((cells, i) => ({ cells, line: i + 1 }))
      .filter((r) => r.cells.some((c) => c.trim() !== ""));
    const all = numbered.map((r) => r.cells);
    if (all.length === 0) return setError("That file looks empty.");
    const header = looksLikeHeader(all[0]!);
    const width = Math.max(...all.map((r) => r.length));
    const headers = header
      ? all[0]!.map((h, i) => h || `Column ${i + 1}`)
      : Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
    setCsvHasHeader(header);
    setMapping(null);
    const body = header ? numbered.slice(1) : numbered;
    setCsvTable({ headers, rows: body.map((r) => r.cells), lines: body.map((r) => r.line) });
  };

  const mapped = useMemo(
    () => (table ? table.rows.map((r) => mapRow(r, mapping)) : []),
    [table, mapping],
  );
  const good = mapped.filter((m) => m.kind === "row").length;
  const bad = mapped.filter((m) => m.kind === "error").length;
  const hasName = mapping.some(
    (k) => k === "firstName" || k === "fullName" || k === "nameAndPhone" || k === "company",
  );

  const lineOf = (i: number) => table?.lines?.[i] ?? i + (source === "csv" ? 2 : 1);

  const run = useMutation({
    mutationFn: async () => {
      const rows: Array<{ row: ImportRow; line: number }> = [];
      mapped.forEach((m, i) => {
        if (m.kind === "row") rows.push({ row: m.row, line: lineOf(i) });
      });
      const total: ImportResult = {
        created: 0,
        updated: 0,
        skipped: 0,
        propertiesAdded: 0,
        errors: [],
      };
      mapped.forEach((m, i) => {
        if (m.kind === "error") total.errors.push({ row: lineOf(i), message: m.message });
      });
      setProgress({ done: 0, total: rows.length });
      for (let i = 0; i < rows.length; i += IMPORT_BATCH_MAX) {
        const batch = rows.slice(i, i + IMPORT_BATCH_MAX);
        const res = await api.post(
          "/import/customers",
          { mode, source, firstRowNumber: batch[0]!.line, rows: batch.map((b) => b.row) },
          importResultSchema,
        );
        total.created += res.created;
        total.updated += res.updated;
        total.skipped += res.skipped;
        total.propertiesAdded += res.propertiesAdded;
        total.errors.push(...res.errors);
        setProgress({ done: Math.min(rows.length, i + batch.length), total: rows.length });
      }
      return total;
    },
    onSuccess: async (res) => {
      setResult(res);
      setProgress(null);
      startOver();
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => {
      setProgress(null);
      setError(
        `${messageFor(err)} Customers already saved stay saved; you can run the file again with "Skip them".`,
      );
    },
  });

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Import customers" help={<HelpIcon screen="import" />} />
      {shownError ? <Banner tone="danger">{shownError}</Banner> : null}
      {result ? (
        <Card title="Import finished" className="mb-6">
          <ul className="space-y-1">
            <li>{result.created} new customers</li>
            <li>{result.updated} existing customers updated</li>
            <li>{result.skipped} skipped because they were already here</li>
            <li>
              {result.propertiesAdded} addresses added (we look them up on the map in the
              background)
            </li>
          </ul>
          {result.errors.length > 0 ? (
            <div className="mt-3">
              <p className="font-medium">{result.errors.length} rows could not be imported:</p>
              <ul className="mt-1 max-h-48 overflow-y-auto text-sm">
                {result.errors.map((e) => (
                  <li key={`${e.row}-${e.message}`}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <Link to="/customers" className="mt-3 inline-block text-brand-600 underline">
            See your customers
          </Link>
        </Card>
      ) : null}

      {!table ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="From a spreadsheet">
            <p className="mb-3 text-base">
              Save your spreadsheet as CSV (in Excel or Google Sheets: File, Download, CSV), then
              choose it here.
            </p>
            <label className="inline-flex min-h-11 cursor-pointer items-center rounded-lg bg-brand-600 px-4 text-white hover:bg-brand-700">
              Choose a CSV file
              <input
                type="file"
                accept=".csv,text/csv,.txt"
                className="sr-only"
                onChange={(e) => e.target.files?.[0] && void readFile(e.target.files[0])}
              />
            </label>
          </Card>
          <Card title="From Google Contacts">
            {google.data?.configured ? (
              <>
                <p className="mb-3 text-base">
                  Sign in with Google and we read your contacts once. Nothing is saved until you
                  import.
                </p>
                <Button onClick={() => startGoogle.mutate()} busy={startGoogle.isPending}>
                  Connect Google
                </Button>
              </>
            ) : (
              <p className="text-muted">Google import is not available yet.</p>
            )}
            {googleTable.isLoading ? <Spinner /> : null}
          </Card>
        </div>
      ) : (
        <>
          <Card title="1. Tell us what each column is" className="mb-4">
            <div className="grid gap-3 md:grid-cols-3">
              {table.headers.map((h, i) => (
                <div key={`${h}-${i}`} className="rounded-lg border border-line p-3">
                  <label htmlFor={`col-${i}`} className="block truncate text-sm font-medium">
                    {h}
                  </label>
                  <p className="mb-2 truncate text-xs text-muted">
                    e.g. {table.rows.find((r) => r[i]?.trim())?.[i] ?? "(empty)"}
                  </p>
                  <Select
                    id={`col-${i}`}
                    value={mapping[i] ?? "ignore"}
                    onChange={(e) =>
                      setMapping(
                        mapping.map((k, j) => (j === i ? (e.target.value as ImportColumnKind) : k)),
                      )
                    }
                  >
                    {IMPORT_COLUMN_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {KIND_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
            {!hasName ? (
              <Banner tone="warn">Pick the column with the customer's name.</Banner>
            ) : null}
          </Card>

          <Card
            title={`2. Check the first 20 rows (${good} ready, ${bad} with problems)`}
            className="mb-4"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-muted">
                    <th className="p-2">Row</th>
                    <th className="p-2">Name</th>
                    <th className="p-2">Phone</th>
                    <th className="p-2">Email</th>
                    <th className="p-2">Address</th>
                    <th className="p-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {mapped.slice(0, 20).map((m, i) => (
                    <tr key={i} className="border-t border-line align-top">
                      <td className="p-2 text-muted">{i + (source === "csv" ? 2 : 1)}</td>
                      {m.kind === "row" ? (
                        <>
                          <td className="p-2">
                            {[m.row.firstName, m.row.lastName].filter(Boolean).join(" ")}
                          </td>
                          <td className="p-2">
                            {m.row.phones?.map((p) => formatPhone(p)).join(", ")}
                          </td>
                          <td className="p-2">{m.row.email}</td>
                          <td className="p-2">
                            {m.row.property
                              ? `${m.row.property.line1}, ${m.row.property.city}`
                              : ""}
                          </td>
                          <td className="p-2">
                            {m.warnings.map((w) => (
                              <div key={w}>
                                <Badge tone="warn">{w}</Badge>
                              </div>
                            ))}
                          </td>
                        </>
                      ) : m.kind === "error" ? (
                        <td className="p-2" colSpan={5}>
                          <Badge tone="danger">{m.message}</Badge>
                        </td>
                      ) : (
                        <td className="p-2 text-muted" colSpan={5}>
                          Blank row, skipped
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="3. If a customer is already here (same phone or email)" className="mb-4">
            <div role="radiogroup" className="space-y-2">
              {DUPLICATE_MODES.map((m) => (
                <label key={m} className="flex min-h-11 items-start gap-3">
                  <input
                    type="radio"
                    name="dup-mode"
                    className="mt-1 h-5 w-5"
                    checked={mode === m}
                    onChange={() => setMode(m)}
                  />
                  <span>
                    <span className="font-medium">{MODE_LABEL[m].label}</span>
                    <span className="block text-sm text-muted">{MODE_LABEL[m].help}</span>
                  </span>
                </label>
              ))}
            </div>
          </Card>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => run.mutate()}
              busy={run.isPending}
              disabled={!hasName || good === 0}
            >
              Import {good} customers
            </Button>
            <Button variant="secondary" onClick={startOver} disabled={run.isPending}>
              Start over
            </Button>
            {progress ? (
              <span className="text-sm text-muted" aria-live="polite">
                {progress.done} of {progress.total} done
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
